import { Grid, Step, StepButton, StepContent, Stepper } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { SurveyService } from "../../api/SurveyService";
import useTranslation from "../../hooks/useTranslation";
import SurveyDispatch from "../../models/surveyDispatch";
import {
  GetSurveyJobLevels,
  SurveyEmailLanguage,
  SurveyEmailResponse,
} from "../../types/survey";
import { CustomQuestionsOverview } from "../CustomQuestionsOverview/CustomQuestionsOverview";
import { SurveyIntroductionEmail } from "../SurveyIntroductionEmail/SurveyIntroductionEmail";
import { SurveyLaunchJobRoles } from "../SurveyLaunchJobRoles/SurveyLaunchJobRoles";
import { SurveyPreviewCard } from "../SurveyPreviewCard/SurveyPreviewCard";
import { SurveyRecipients } from "../SurveyRecipients/SurveyRecipients";
import { SurveySettings } from "../SurveySettings/SurveySettings";
import { SurveySummary } from "../SurveySummary/SurveySummary";
import { Button } from "../ui/Button/Button";
import { Card } from "../ui/Card/Card";
import "./SurveyLaunchSteps.scss";

export interface SurveyLaunchStepsProps {
  onClose: () => void;
  surveyDispatch: SurveyDispatch;
  onSurveyUpdate: () => void;
}
export const SurveyLaunchSteps = ({
  surveyDispatch,
  onClose,
  onSurveyUpdate,
}: SurveyLaunchStepsProps) => {
  const { t } = useTranslation("surveyLaunchPage");
  const [step, setStep] = useState(0);

  const surveyService = new SurveyService();
  const {
    data: dispatchJobRolesData,
    isLoading: dispatchJobRolesIsLoading,
    error: dispatchJobRolesError,
    refetch: jobRolesRefetch,
  } = useQuery(
    [`jobRoles-${surveyDispatch.id}`],
    () => surveyService.getDispatchJobRoles(surveyDispatch),
    { retry: false, enabled: !!surveyDispatch.id }
  );

  const {
    data: surveyJobLevels,
    isInitialLoading: isLoadingSurveyJobLevels,
    error: surveyJobLevelsError,
  } = useQuery<GetSurveyJobLevels, AxiosError>(
    ["jobLevels"],
    () => surveyService.getSurveyJobLevels(surveyDispatch.surveyId),
    { enabled: !!surveyDispatch.surveyId }
  );

  // get all available survey email languages from api
  const {
    data: availableSurveyEmailLanguages,
    isLoading: availableSurveyEmailLanguagesIsLoading,
    error: availableSurveyEmailLanguagesError,
  } = useQuery<SurveyEmailLanguage[], AxiosError>(
    ["availableSurveyEmailLanguages"],
    () =>
      surveyService.getSupportedSurveyEmailLanguages(surveyDispatch.surveyId),
    { enabled: !!surveyDispatch.id && !!surveyDispatch.surveyId }
  );

  const {
    data: existingSurveyEmail,
    isLoading: surveyEmailIsLoading,
    error: surveyEmailError,
    refetch: refetchSurveyEmail,
  } = useQuery<SurveyEmailResponse, AxiosError>(
    [`surveyEmail-${surveyDispatch.id}`],
    () => surveyService.getSurveyEmail(surveyDispatch),
    { enabled: !!surveyDispatch.id }
  );

  const loadingEmailData =
    surveyEmailIsLoading || availableSurveyEmailLanguagesIsLoading;

  const handleNext = () => {
    if (step === steps.length - 1) {
      onClose();
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };
  const handleStep = (step: number) => () => {
    setStep(step);
  };
  const steps = [
    t("steps.firstPreview"),
    t("steps.customiseJobRoles"),
    t("steps.customQuestion"),
    t("steps.preview"),
    t("steps.previewEmail"),
    t("steps.selectRecipients"),
    t("steps.dueDate"),
    t("steps.summary"),
  ];

  const renderSteps = () => {
    switch (step) {
      case 0:
        return (
          <SurveyPreviewCard
            key="survey-preview-card"
            isLoading={!surveyDispatch}
            dispatchId={surveyDispatch.id}
            firstPreview
            isSetup
          />
        );
      case 1:
        return (
          <SurveyLaunchJobRoles
            key="survey-launch-job-roles"
            dispatchJobRoles={dispatchJobRolesData}
            isLoading={dispatchJobRolesIsLoading || isLoadingSurveyJobLevels}
            error={!!dispatchJobRolesError || !!surveyJobLevelsError}
            refetch={jobRolesRefetch}
            dispatch={surveyDispatch}
            jobLevels={surveyJobLevels}
          />
        );
      case 2:
        return (
          <CustomQuestionsOverview
            key="custom-questions"
            dispatch={surveyDispatch}
          />
        );
      case 3:
        return (
          <SurveyPreviewCard
            key="survey-preview-card-setup"
            isLoading={!surveyDispatch}
            dispatchId={surveyDispatch.id}
            isSetup
          />
        );
      case 4:
        return (
          <SurveyIntroductionEmail
            key="survey-introduction-email"
            availableSurveyEmailLanguages={availableSurveyEmailLanguages}
            errorLoadingData={
              !!availableSurveyEmailLanguagesError || !!surveyEmailError
            }
            existingSurveyEmail={existingSurveyEmail}
            loadingData={loadingEmailData}
            surveyDispatch={surveyDispatch}
            refetchSurveyEmail={refetchSurveyEmail}
          />
        );
      case 5:
        return (
          <SurveyRecipients
            key={`survey-recipients-${step}`}
            onSurveyUpdate={onSurveyUpdate}
            surveyDispatch={surveyDispatch}
            isLoading={false}
            error={false}
          />
        );
      case 6:
        return (
          surveyDispatch && (
            <SurveySettings dispatch={surveyDispatch} key="survey-settings" />
          )
        );
      case 7:
        return <SurveySummary />;
      default:
        return <></>;
    }
  };
  return (
    <Grid container spacing={3} marginBottom={3}>
      <Grid item sm={0} md={1} />
      <Grid item sm={12} md={10}>
        <Card className="SurveyLaunchSteps">
          <Stepper nonLinear activeStep={step} orientation="vertical">
            {steps.map((label, index) => {
              return (
                <Step key={label}>
                  <StepButton
                    onClick={handleStep(index)}
                    disableRipple
                    sx={{ fontSize: "1.4rem" }}
                  >
                    {label}
                  </StepButton>
                  <StepContent className="SurveyLaunchSteps__panel">
                    {renderSteps()}
                    <div className="SurveyLaunchSteps__actions">
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleBack}
                        disabled={step === 0}
                        small
                      >
                        {t("actions.back")}
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        onClick={handleNext}
                        small
                      >
                        {step === steps.length - 1
                          ? t("actions.finish")
                          : t("actions.next")}
                      </Button>
                    </div>
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        </Card>
      </Grid>
      <Grid item sm={12} md={1} />
    </Grid>
  );
};
